import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'gatsby'
import { kebabCase } from 'lodash'
import styled from 'styled-components'
import { LiveTv } from '@material-ui/icons'
import IconButton from '@material-ui/core/IconButton'
import OddsButton from './OddsButton'
import ContextConsumer from './Context'
import Icons from './Icons'
import { addToSlip, removeFromSlip } from './Betslip/utilities.js'

const FixtureCardWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
  align-content: center;
  padding: ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(4)}px ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(4)}px;
  width: 100%;
  min-width: 320px;
  background-color: ${props => props.theme.palette.background.paper};
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0),
    0px 2px 2px 0px rgba(0, 0, 0, 0), 0px 1px 5px 0px rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);

  & ~ & {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    &:last-child {
      margin-bottom: ${props => props.theme.spacing(2)}px;
      border-bottom: 1px solid rgba(0, 0, 0, 0);
    }
  }
  @media (max-width: 1300px) {
    flex-direction: column;
    margin-bottom: ${props => props.theme.spacing(2)}px;
    border-bottom: 0px solid rgba(0, 0, 0, 0);
  }
`
const Primary = styled.div`
  padding: ${props => props.theme.spacing(0)}px
    ${props => props.theme.spacing(6)}px ${props => props.theme.spacing(0)}px
    ${props => props.theme.spacing(2)}px;
  border-top-left-radius: ${props => props.theme.spacing(2)}px;
  border-top-right-radius: ${props => props.theme.spacing(2)}px;

  @media (max-width: 1300px) {
    padding: ${props => props.theme.spacing(0)}px;
    margin-bottom: ${props => props.theme.spacing(4)}px;
  }
`
const SmallFixtureCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding: ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(6)}px ${props => props.theme.spacing(4)}px
    ${props => props.theme.spacing(6)}px;
  margin-bottom: ${props => props.theme.spacing(2)}px;
  min-width: 320px;
  width: 100%;
  background-color: ${props => props.theme.palette.background.paper};
  border-bottom: 0 solid rgba(0, 0, 0, 0);
  box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0),
    0px 2px 2px 0px rgba(0, 0, 0, 0), 0px 1px 5px 0px rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
`
const SmallPrimary = styled.div`
  padding: ${props => props.theme.spacing(0)}px;
  margin-bottom: ${props => props.theme.spacing(4)}px;
  border-top-left-radius: ${props => props.theme.spacing(2)}px;
  border-top-right-radius: ${props => props.theme.spacing(2)}px;
`
const HorizontalBlock = styled.div`
  display: flex;
  width: 100%;
`
const Actions = styled.div`
  width: 100%;
  display: grid;
  grid-column-gap: ${props => props.theme.spacing(2)}px;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  padding: ${props => props.theme.spacing(0)}px;
`
const HorizontalActions = styled.div`
  min-width: 50%;
  height: 48px;
  display: grid;
  grid-column-gap: ${props => props.theme.spacing(2)}px;
  grid-template-columns: repeat(auto-fit, minmax(90px, 1fr));
  padding: ${props => props.theme.spacing(0)}px
    ${props => props.theme.spacing(4)}px ${props => props.theme.spacing(0)}px
    ${props => props.theme.spacing(0)}px;

  @media (max-width: 1300px) {
    width: 100%;
    padding: ${props => props.theme.spacing(0)}px;
  }
`
const FixtureSubheader = styled.div`
  flex: 1 100%;
  font-size: 0.75rem;
  font-weight: bold;
  text-transform: uppercase;
  color: ${props => props.theme.palette.text.secondary};
  margin-top: -${props => props.theme.spacing(2)}px;
`
const FixtureCompetitorNames = styled.div`
  flex: 1;
`
const FixtureMarketInformation = styled.div`
  text-align: right;
  justify-content: flex-end;
  margin-top: ${props => props.theme.spacing(1)}px;
`
const CompetitorName = styled.div`
  font-size: 1rem;
  letter-spacing: 0.01786em;
  color: ${props => props.theme.palette.text.primary};
`
const FixtureTime = styled.div`
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: bold;
  color: ${props => props.theme.palette.text.secondary};
`
const FixtureStatusCount = styled.div`
  display: flex;
  margin-left: auto;
`
const FixtureMarketCount = styled.div`
  margin-left: auto;
  margin-top: ${props => props.theme.spacing(1)}px;
  font-size: 0.75rem;
  font-weight: bold;
  color: ${props => props.theme.palette.text.secondary};
`
const FixtureScores = styled.div`
  padding: ${props => props.theme.spacing(0)}px
    ${props => props.theme.spacing(4)}px;
`
const CompetitorScore = styled.div`
  font-size: 1rem;
  letter-spacing: 0.01786em;
  font-weight: bold;
  color: ${props => props.theme.palette.primary.main};
`
const LiveIcon = styled(LiveTv)`
  width: 0.668em;
  height: 0.668em;
  margin-top: ${props => props.theme.spacing(1)}px;
  margin-right: ${props => props.theme.spacing(1)}px;
  color: ${props => props.theme.palette.primary.main};
`
const LiveIconButton = styled(IconButton)`
  @media (max-width: 1300px) {
    display: none !important;
  }
`

const FixtureCard = props => {
  // oddsButtons State
  const isDisabled = false

  const {
    fixture: {
      id,
      slug,
      sport,
      competition,
      start,
      date,
      status,
      marketCount,
      team1title,
      team2title,
      gametime,
      gameperiod,
      gamescore,
      markets,
    },
    subheader,
    inplay,
    size,
    court,
  } = props

  // Just get the first market to display match result
  const matchResult = Object.keys(markets).slice(0, 1)

  return (
    <ContextConsumer>
      {({ data, set }) => {
        const renderMarket = marketIndex => {
          const market = markets[marketIndex]
          const linkTo = inplay
            ? `/in-play/${kebabCase(sport)}/${kebabCase(
                competition
              )}/${kebabCase(slug)}-${kebabCase(id)}/`
            : `/${kebabCase(sport)}/${kebabCase(competition)}/${kebabCase(
                slug
              )}-${kebabCase(id)}/`

          if (size === 'small') {
            return (
              <SmallFixtureCardWrapper key={`${id}-${marketIndex}`}>
                <Link
                  to={linkTo}
                  style={{
                    color: '#000',
                    textDecoration: 'none',
                    width: '100%',
                  }}
                >
                  <SmallPrimary>
                    {subheader && (
                      <FixtureSubheader>
                        <Icons
                          icon={sport}
                          style={{
                            fontSize: 16,
                            marginRight: '4px',
                            marginBottom: '-2px',
                          }}
                        />
                        {competition}
                      </FixtureSubheader>
                    )}
                    <HorizontalBlock>
                      <FixtureCompetitorNames>
                        <CompetitorName>{team1title}</CompetitorName>
                        <CompetitorName>{team2title}</CompetitorName>
                      </FixtureCompetitorNames>
                      {status === 'inplay' && (
                        <FixtureScores>
                          <CompetitorScore>{gamescore[0]}</CompetitorScore>
                          <CompetitorScore>{gamescore[1]}</CompetitorScore>
                        </FixtureScores>
                      )}
                      <FixtureMarketInformation>
                        {status === 'inplay' && (
                          <FixtureTime>
                            {gametime} {gameperiod}
                          </FixtureTime>
                        )}
                        {status === 'upcoming' && (
                          <FixtureTime>{start}</FixtureTime>
                        )}
                        <FixtureStatusCount>
                          {status === 'inplay' && <LiveIcon />}
                          <FixtureMarketCount>
                            +{marketCount}
                          </FixtureMarketCount>
                        </FixtureStatusCount>
                      </FixtureMarketInformation>
                    </HorizontalBlock>
                  </SmallPrimary>
                </Link>

                <Actions>
                  {Object.keys(market.outcomes).map(buttonIndex => {
                    const isSelected =
                      data.betslip[`${id}-${marketIndex}-${buttonIndex}`]
                    const outcome = market.outcomes[buttonIndex]

                    return (
                      <OddsButton
                        id={id}
                        key={`${id}-${marketIndex}-${buttonIndex}`}
                        value={parseFloat(outcome.odds)}
                        teamTitle={outcome.name}
                        isDisabled={isDisabled}
                        isSelected={isSelected}
                        clickHandler={() =>
                          !isSelected
                            ? addToSlip(
                                data,
                                set,
                                id,
                                marketIndex,
                                buttonIndex,
                                sport,
                                competition,
                                market.title,
                                outcome.name,
                                outcome.odds,
                                slug,
                                date,
                                start
                              )
                            : removeFromSlip(
                                data,
                                set,
                                id,
                                marketIndex,
                                buttonIndex
                              )
                        }
                      />
                    )
                  })}
                </Actions>
              </SmallFixtureCardWrapper>
            )
          }

          return (
            <FixtureCardWrapper key={`${id}-${marketIndex}`}>
              <Link
                to={linkTo}
                style={{
                  color: '#000',
                  textDecoration: 'none',
                  width: '100%',
                }}
              >
                <Primary>
                  {subheader && (
                    <FixtureSubheader>
                      <Icons
                        icon={sport}
                        style={{
                          fontSize: 16,
                          marginRight: '4px',
                          marginBottom: '-4px',
                          marginTop: '4px',
                        }}
                      />
                      {competition}
                    </FixtureSubheader>
                  )}
                  <HorizontalBlock>
                    <FixtureCompetitorNames>
                      <CompetitorName>{team1title}</CompetitorName>
                      <CompetitorName>{team2title}</CompetitorName>
                    </FixtureCompetitorNames>
                    {status === 'inplay' && (
                      <FixtureScores>
                        <CompetitorScore>{gamescore[0]}</CompetitorScore>
                        <CompetitorScore>{gamescore[1]}</CompetitorScore>
                      </FixtureScores>
                    )}
                    <FixtureMarketInformation>
                      {status === 'inplay' && (
                        <FixtureTime>
                          {gametime} {gameperiod}
                        </FixtureTime>
                      )}
                      {status === 'upcoming' && (
                        <FixtureTime>{start}</FixtureTime>
                      )}
                      <FixtureStatusCount>
                        {status === 'inplay' && <LiveIcon />}
                        <FixtureMarketCount>+{marketCount}</FixtureMarketCount>
                      </FixtureStatusCount>
                    </FixtureMarketInformation>
                  </HorizontalBlock>
                </Primary>
              </Link>

              <HorizontalActions>
                {Object.keys(market.outcomes).map(buttonIndex => {
                  const isSelected =
                    data.betslip[`${id}-${marketIndex}-${buttonIndex}`]
                  const outcome = market.outcomes[buttonIndex]

                  return (
                    <OddsButton
                      id={id}
                      key={`${id}-${marketIndex}-${buttonIndex}`}
                      value={parseFloat(outcome.odds)}
                      teamTitle={outcome.name}
                      isDisabled={isDisabled}
                      isSelected={isSelected}
                      clickHandler={() =>
                        !isSelected
                          ? addToSlip(
                              data,
                              set,
                              id,
                              marketIndex,
                              buttonIndex,
                              sport,
                              competition,
                              market.title,
                              outcome.name,
                              outcome.odds,
                              slug,
                              date,
                              start
                            )
                          : removeFromSlip(
                              data,
                              set,
                              id,
                              marketIndex,
                              buttonIndex
                            )
                      }
                    />
                  )
                })}
              </HorizontalActions>

              {status === 'inplay' && court === 'display' && (
                <LiveIconButton component={Link} to={linkTo}>
                  <Icons
                    icon="court"
                    style={{
                      fontSize: 24,
                      marginRight: '4px',
                      marginBottom: '6px',
                    }}
                  />
                </LiveIconButton>
              )}
            </FixtureCardWrapper>
          )
        }
        return matchResult.map(renderMarket)
      }}
    </ContextConsumer>
  )
}

FixtureCard.propTypes = {
  fixture: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    sport: PropTypes.string.isRequired,
    competition: PropTypes.string.isRequired,
    start: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    marketCount: PropTypes.string.isRequired,
    team1title: PropTypes.string.isRequired,
    team2title: PropTypes.string.isRequired,
    gametime: PropTypes.any,
    gameperiod: PropTypes.any,
    gamescore: PropTypes.any,
    markets: PropTypes.array.isRequired,
  }),
  subheader: PropTypes.bool,
  status: PropTypes.string,
  inplay: PropTypes.any,
  size: PropTypes.string,
  court: PropTypes.string,
}

export default FixtureCard
